/**
 * <ComplianceCheckPanel>
 *
 * Render above the Send button in any composer (bulk email, bulk SMS, 1-1).
 * Calls /api/communications/validate-send on draft change (debounced) and on
 * the Send click. Surfaces blocks/warnings, and (if user has override perm)
 * captures an override reason that goes into the audit log.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Shield } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  communicationValidateAPI,
  ValidateSendResult,
  ValidateBlock,
} from '@/services/api/communicationValidate';
import { useComplianceMode } from '@/hooks/useComplianceMode';

interface Props {
  templateId?: number;
  assetIds?: number[];
  memberIds?: number[];
  channel: 'email' | 'sms' | 'voice' | 'mail' | 'whatsapp';
  sampleOnly?: boolean;
  /** Called whenever the panel's gate state changes — composer disables Send when ok=false. */
  onGateChange?: (gate: { ok: boolean; result: ValidateSendResult | null }) => void;
}

export function ComplianceCheckPanel({
  templateId,
  assetIds,
  memberIds,
  channel,
  sampleOnly,
  onGateChange,
}: Props) {
  const { isRegulated, canOverrideBlock } = useComplianceMode();
  const [result, setResult] = useState<ValidateSendResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideRecorded, setOverrideRecorded] = useState(false);
  const debounceRef = useRef<number | null>(null);

  const payload = useMemo(
    () => ({ template_id: templateId, asset_ids: assetIds, member_ids: memberIds, channel, sample_only: sampleOnly }),
    [templateId, JSON.stringify(assetIds), JSON.stringify(memberIds), channel, sampleOnly],
  );

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        const r = await communicationValidateAPI.validateSend(payload);
        setResult(r);
        onGateChange?.({ ok: r.ok || overrideRecorded, result: r });
      } catch {
        // network error — fail open with a generic warning
        setResult({
          ok: true, mode: 'standard',
          blocks: [], warnings: [{ kind: 'consent', subject_id: 0, reason: 'validate_check_unavailable', severity: 'warning' }],
          checked: { template: !!payload.template_id, assets: payload.asset_ids?.length || 0, members: payload.member_ids?.length || 0, channel, sampled: !!sampleOnly },
        });
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [JSON.stringify(payload), overrideRecorded]);

  if (!result) {
    return (
      <Card>
        <CardContent className="py-3 text-sm text-gray-500 flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" /> Checking compliance…
        </CardContent>
      </Card>
    );
  }

  const allClear = result.ok || overrideRecorded;

  return (
    <Card className={allClear ? 'border-green-200' : 'border-red-300'}>
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center gap-2">
          {allClear ? (
            <>
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium">Compliance check passed</span>
              <Badge variant="outline" className="ml-1 text-xs">
                {result.mode === 'regulated' ? 'Regulated entity' : 'Standard mode'}
              </Badge>
              {overrideRecorded && (
                <Badge className="bg-amber-100 text-amber-900 ml-1">Override recorded</Badge>
              )}
            </>
          ) : (
            <>
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <span className="text-sm font-semibold text-red-700">Compliance check failed</span>
              <Badge className="bg-amber-100 text-amber-900 ml-1">
                {isRegulated ? 'Regulated entity' : 'Standard mode'}
              </Badge>
              {loading && <Loader2 className="h-3 w-3 animate-spin ml-2 text-gray-400" />}
            </>
          )}
        </div>

        {!allClear && result.blocks.length > 0 && (
          <Alert variant="destructive">
            <AlertDescription>
              <div className="font-medium mb-1">{result.blocks.length} issue(s) must be resolved before sending:</div>
              <ul className="list-disc ml-5 space-y-1 text-sm">
                {result.blocks.map((b, i) => (
                  <li key={i}>{describeBlock(b)}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {result.warnings.length > 0 && (
          <Alert>
            <AlertDescription>
              <div className="font-medium mb-1">{result.warnings.length} warning(s):</div>
              <ul className="list-disc ml-5 space-y-1 text-xs">
                {result.warnings.map((b, i) => (
                  <li key={i}>{describeBlock(b)}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {!allClear && canOverrideBlock && result.blocks.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950 dark:border-amber-900 p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
              <Shield className="h-3 w-3" /> Compliance override (admin only)
            </div>
            <Textarea
              placeholder="Explain why this send should proceed despite the block. This reason is recorded in the audit log."
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              rows={2}
              className="text-sm"
            />
            <Button
              size="sm"
              variant="outline"
              disabled={!overrideReason.trim()}
              onClick={async () => {
                await communicationValidateAPI.overrideBlock({
                  template_id: templateId,
                  asset_ids: assetIds,
                  blocks: result.blocks,
                  reason: overrideReason,
                });
                setOverrideRecorded(true);
                onGateChange?.({ ok: true, result });
              }}
            >
              Record override and proceed
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function describeBlock(b: ValidateBlock): string {
  switch (b.kind) {
    case 'template':
      return `Template ${b.subject_name ? `"${b.subject_name}"` : `#${b.subject_id}`}: ${humanizeReason(b.reason)}`;
    case 'media_asset':
      return `Media asset #${b.subject_id}: ${humanizeReason(b.reason)}${b.expiration_date ? ` (expired ${b.expiration_date})` : ''}`;
    case 'consent':
      return `Member #${b.subject_id} ${b.channel ? `(${b.channel})` : ''}: ${humanizeReason(b.reason)}`;
    default:
      return `${b.kind}: ${humanizeReason(b.reason)}`;
  }
}

function humanizeReason(reason: string): string {
  return reason.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

export default ComplianceCheckPanel;
